import { User } from '@/app/shared/interfaces';
import { Component, inject, Output, EventEmitter } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { NgIf } from '@angular/common';
import { Store } from '@ngrx/store';
import { AuthSelectors } from '@/app/+state/auth';
import { SvgIconComponent } from 'angular-svg-icon';
import { UserRole } from '@qc/shared';
import { SidebarService } from '@/app/core/services/sidebar.service';
import { NotificationsService } from '@/app/core/services/notifications.service';

interface LinkItem {
    icon?: string;
    title: string;
    link: string[];
    exactLink?: boolean;
    hasPermission?: boolean;
    children?: LinkItem[];
}

@Component({
    selector: 'qc-navigation',
    standalone: true,
    imports: [SvgIconComponent, RouterLink, RouterLinkActive, NgIf],
    template: `
        <nav class="qc-nav" [class.expanded]="!sidebar.collapsed()" *ngIf="user">
            <ul>
                @for (menuItem of menuItems; track menuItem) {
                    <li *ngIf="menuItem.hasPermission === undefined || menuItem.hasPermission">
                        <a
                            class="nav-item"
                            [routerLink]="menuItem.link"
                            routerLinkActive="nav-item--active"
                            [routerLinkActiveOptions]="{
                                exact: menuItem?.exactLink || false,
                            }"
                            (click)="onMenuItemClick()"
                            [title]="badgeTitleFor(menuItem)"
                            #rla="routerLinkActive"
                        >
                            <span class="icon-wrap">
                                <svg-icon [src]="'icons/' + menuItem.icon + '.svg'"></svg-icon>
                                <span class="dot-badge"
                                      *ngIf="menuItem.icon === 'bell' && notifications.unreadCount() > 0 && sidebar.collapsed()">
                                </span>
                            </span>
                            <span class="label" *ngIf="!sidebar.collapsed()">{{ menuItem.title }}</span>
                            <span class="count-badge"
                                  *ngIf="!sidebar.collapsed() && menuItem.icon === 'bell' && notifications.unreadCount() > 0">
                                {{ notifications.unreadCount() }}
                            </span>
                        </a>
                        <ul class="nav-children"
                            *ngIf="!sidebar.collapsed() && menuItem.children && menuItem.children.length && rla.isActive">
                            @for (menuItemChildren of menuItem.children; track menuItemChildren) {
                                <li *ngIf="menuItemChildren.hasPermission === undefined || menuItemChildren.hasPermission">
                                    <a
                                        class="nav-child"
                                        [routerLink]="menuItemChildren.link"
                                        routerLinkActive="nav-child--active"
                                        [routerLinkActiveOptions]="{ exact: menuItemChildren?.exactLink || false }"
                                        (click)="onMenuItemClick()">
                                        <span>{{ menuItemChildren.title }}</span>
                                    </a>
                                </li>
                            }
                        </ul>
                    </li>
                }
            </ul>
        </nav>
    `,
    styles: [`
        :host { display: flex; flex-direction: column; min-height: 0; }

        .qc-nav {
            color: #ffffff;
            padding: 0 10px;
            display: flex;
            flex-direction: column;
            min-height: 0;
            flex: 1;
        }
        .qc-nav ul {
            list-style: none;
            padding: 0;
            margin: 0;
            display: flex;
            flex-direction: column;
            gap: 2px;
        }

        .nav-item {
            display: flex;
            align-items: center;
            height: 44px;
            color: rgba(255,255,255,0.78);
            text-decoration: none;
            border-radius: 8px;
            position: relative;
            cursor: pointer;
            transition: background 120ms ease, color 120ms ease;
        }
        .qc-nav:not(.expanded) .nav-item {
            justify-content: center;
            width: 44px;
            margin: 0 auto;
        }
        .qc-nav.expanded .nav-item {
            padding: 0 12px;
            gap: 12px;
            width: 100%;
        }
        .nav-item:hover { background: rgba(255,255,255,0.06); color: #ffffff; }
        .nav-item.nav-item--active { background: rgba(255,255,255,0.14); color: #ffffff; }
        .nav-item.nav-item--active::before {
            content: '';
            position: absolute;
            left: -10px;
            top: 10px;
            bottom: 10px;
            width: 3px;
            background: #ffffff;
            border-radius: 0 2px 2px 0;
        }

        .nav-item svg-icon {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 18px;
            height: 18px;
            flex-shrink: 0;
        }
        .nav-item ::ng-deep svg {
            width: 20px;
            height: 20px;
            display: block;
        }
        /* QC icon set uses stroke="currentColor" — let inheritance do the work,
           don't force fill or it'll turn the outline icons into solid blobs. */

        .label {
            font-size: 13.5px;
            font-weight: 500;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        .icon-wrap {
            position: relative;
            display: inline-flex;
            align-items: center;
            justify-content: center;
        }
        .dot-badge {
            position: absolute;
            top: -3px;
            right: -4px;
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background: #ef4444;
            box-shadow: 0 0 0 1.5px #1f7a3a;
        }
        .count-badge {
            margin-left: auto;
            min-width: 20px;
            text-align: center;
            font-size: 10.5px;
            font-weight: 700;
            line-height: 18px;
            padding: 0 6px;
            border-radius: 999px;
            background: #ef4444;
            color: #ffffff;
        }

        .nav-children { margin: 4px 0 8px 38px; }
        .nav-child {
            display: flex;
            padding: 6px 8px;
            font-size: 12.5px;
            color: rgba(255,255,255,0.7);
            border-radius: 6px;
            text-decoration: none;
        }
        .nav-child:hover { color: #ffffff; background: rgba(255,255,255,0.06); }
        .nav-child.nav-child--active { color: #ffffff; }
    `],
})
export class NavigationComponent {
    @Output() closeMenu = new EventEmitter<void>();
    readonly sidebar = inject(SidebarService);
    readonly notifications = inject(NotificationsService);

    public menuItems: LinkItem[] = [];
    public user: User | null = null;

    constructor(private store: Store) {
        this.store
            .select(AuthSelectors.selectUser)
            .pipe(takeUntilDestroyed())
            .subscribe((user) => {
                if (user === null) return;
                this.user = user;
                this.initMenuItems();
            });
    }

    initMenuItems() {
        if (!this.user) {
            return;
        }
        this.menuItems = [
            {
                icon: 'home',
                title: 'Dashboard',
                link: [''],
                hasPermission: true,
                exactLink: true,
            },
            {
                icon: 'archive',
                title: 'Dispositions',
                link: ['release'],
                hasPermission: true,
            },
            {
                icon: 'briefcase',
                title: 'Clients',
                link: ['clients'],
                hasPermission: true,
                children: [
                    {
                        title: 'Client Information Records',
                        link: ['clients/records'],
                        hasPermission: true,
                    },
                    {
                        title: 'Access Control',
                        link: ['clients/access-control'],
                        hasPermission: true,
                    },
                ],
            },
            {
                icon: 'mpd',
                title: 'MPD',
                link: ['mpd'],
                hasPermission: true,
                children: [
                    {
                        title: 'Products',
                        link: ['mpd/products'],
                        hasPermission: true,
                    },
                    {
                        title: 'Organizations',
                        link: ['mpd/organizations'],
                        hasPermission: true,
                    },
                ],
            },
            {
                icon: 'stability',
                title: 'Annual Activity',
                link: ['stability'],
                hasPermission: true,
                children: [
                    {
                        title: 'Confirmatory Testing',
                        link: ['stability/confirmatory-testing'],
                        hasPermission: true,
                    },
                ],
            },
            {
                icon: 'clipboard',
                title: 'Reports',
                link: ['reports'],
                hasPermission: true,
                children: [
                    {
                        title: 'Shipment Dashboard',
                        link: ['reports/shipments/status-dashboard'],
                        hasPermission: true,
                    },
                    {
                        title: 'Document Completeness',
                        link: ['reports/shipments/document-completeness'],
                        hasPermission: true,
                    },
                    {
                        title: 'Product Database',
                        link: ['reports/products/master-database'],
                        hasPermission: true,
                    },
                    {
                        title: 'Audit Logs',
                        link: ['reports/audit/system-logs'],
                        hasPermission: this.user?.role === UserRole.SYSTEM_ADMIN || this.user?.role === UserRole.AUDITOR,
                    },
                ],
            },
            {
                icon: 'check-square',
                title: 'Compliance',
                link: ['compliance/document-comparison'],
                hasPermission: true,
            },
            {
                icon: 'bell',
                title: 'Notifications',
                link: ['notifications'],
                hasPermission: true,
                children: [
                    {
                        title: 'Security',
                        icon: 'pi pi-shield',
                        link: ['notifications/security'],
                        hasPermission: true,
                    },
                    {
                        title: 'Preferences',
                        icon: 'pi pi-cog',
                        link: ['notifications/preferences'],
                        hasPermission: true,
                    },
                ],
            },
            {
                icon: 'briefcase',
                title: 'Users Management',
                link: ['admin/users'],
                hasPermission: this.user?.role === UserRole.SYSTEM_ADMIN,
            },
        ];
    }

    onMenuItemClick() {
        this.closeMenu.emit();
    }

    badgeTitleFor(item: LinkItem): string {
        if (item.icon === 'bell') {
            const n = this.notifications.unreadCount();
            return n > 0 ? `${item.title} (${n} unread)` : item.title;
        }
        return item.title;
    }
}
